import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/api";

// Mirrors backend DEFAULT_PRINT_PREFERENCES. Kept in sync manually because
// the frontend must still render print previews even when the API call is
// in flight or fails (e.g. user not yet authenticated).
export const DEFAULT_PRINT_PREFERENCES = {
  version: 1,
  default_template_by_doctype: {
    estimate: "mmj-signature",
    invoice: "mmj-signature",
  },
  default_paper_size_by_doctype: {
    estimate: "A5",
    invoice: "A4",
  },
  default_orientation_by_doctype: {
    estimate: "portrait",
    invoice: "portrait",
  },
  default_detail_filters: {
    current_balance: false,
    payments: false,
    payments_this_estimate: false,
    other_balance: false,
    metal_balance: false,
    metal_deposit_pending: false,
    stone_breakdown: false,
    stone_rates: false,
  },
  photos_default: false,
  show_logo_by_doctype: {
    estimate: true,
    invoice: true,
  },
  themes_by_template: {},
  header_overrides_by_template: {},
  block_styles_by_template: {},
  smart_size_by_template: {},
};

const normalize = (raw) => {
  if (!raw || typeof raw !== "object") return { ...DEFAULT_PRINT_PREFERENCES };
  // One-time migration: legacy show_stone_*_by_doctype lift into the unified
  // detail-filter slot so the new Default Detail Filters checklist is the
  // single source of truth.
  const legacyBreakdown = raw.show_stone_breakdown_by_doctype || {};
  const legacyRates = raw.show_stone_rates_by_doctype || {};
  const incomingDetailFilters = raw.default_detail_filters || {};
  const stoneBreakdownLegacy =
    !!legacyBreakdown.estimate || !!legacyBreakdown.invoice;
  const stoneRatesLegacy = !!legacyRates.estimate || !!legacyRates.invoice;
  const mergedDetailFilters = {
    ...DEFAULT_PRINT_PREFERENCES.default_detail_filters,
    ...incomingDetailFilters,
  };
  if (incomingDetailFilters.stone_breakdown === undefined && stoneBreakdownLegacy) {
    mergedDetailFilters.stone_breakdown = true;
  }
  if (incomingDetailFilters.stone_rates === undefined && stoneRatesLegacy) {
    mergedDetailFilters.stone_rates = true;
  }
  return {
    ...DEFAULT_PRINT_PREFERENCES,
    ...raw,
    default_template_by_doctype: {
      ...DEFAULT_PRINT_PREFERENCES.default_template_by_doctype,
      ...(raw.default_template_by_doctype || {}),
    },
    default_paper_size_by_doctype: {
      ...DEFAULT_PRINT_PREFERENCES.default_paper_size_by_doctype,
      ...(raw.default_paper_size_by_doctype || {}),
    },
    default_orientation_by_doctype: {
      ...DEFAULT_PRINT_PREFERENCES.default_orientation_by_doctype,
      ...(raw.default_orientation_by_doctype || {}),
    },
    default_detail_filters: mergedDetailFilters,
    show_logo_by_doctype: {
      ...DEFAULT_PRINT_PREFERENCES.show_logo_by_doctype,
      ...(raw.show_logo_by_doctype || {}),
    },
    themes_by_template: raw.themes_by_template || {},
    header_overrides_by_template: raw.header_overrides_by_template || {},
    block_styles_by_template: raw.block_styles_by_template || {},
    smart_size_by_template: raw.smart_size_by_template || {},
    photos_default: !!raw.photos_default,
  };
};

const PrintPreferencesContext = createContext({
  prefs: DEFAULT_PRINT_PREFERENCES,
  loading: false,
  refresh: async () => {},
  save: async () => {},
});

export const PrintPreferencesProvider = ({ children }) => {
  const [prefs, setPrefs] = useState(DEFAULT_PRINT_PREFERENCES);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/print-preferences");
      setPrefs(normalize(res?.data));
    } catch {
      setPrefs(normalize(null));
    } finally {
      setLoading(false);
    }
  }, []);

  const save = useCallback(
    async (partial) => {
      const next = normalize({ ...prefs, ...(partial || {}) });
      const res = await api.put("/settings/print-preferences", next);
      const updated = normalize(res?.data || next);
      setPrefs(updated);
      return updated;
    },
    [prefs],
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(() => ({ prefs, loading, refresh, save }), [prefs, loading, refresh, save]);

  return <PrintPreferencesContext.Provider value={value}>{children}</PrintPreferencesContext.Provider>;
};

export const usePrintPreferences = () => useContext(PrintPreferencesContext);
